import * as THREE from 'three';

import CameraControlsImpl from 'camera-controls';
import { useThree } from '@react-three/fiber';
import { fitCameraToBounds, getOrientedBoundingBox, Hole, toVec3 } from '@slope/three';
import { focalOffsetXForScreenFrac, getElementByRegex } from '../utils';

export const isCameraControls = (controls: unknown): controls is CameraControlsImpl => {
  return !!(controls && controls instanceof CameraControlsImpl);
};


export const useFitToHoleBound = () => {
  const controls = useThree(({ controls }) => controls);
  const camera = useThree(({ camera }) => camera);
  return async (hole: Hole) => {
    if (!isCameraControls(controls)) {
      return;
    }

    const tee = getElementByRegex(hole, new RegExp('_tee$'));
    const teePos = new THREE.Box3().setFromObject(tee ?? hole).getCenter(new THREE.Vector3());

    const green = getElementByRegex(hole, new RegExp('_green$'));
    const greenPos = new THREE.Box3().setFromObject(green ?? hole).getCenter(new THREE.Vector3());

    const direction = new THREE.Vector3().subVectors(toVec3(greenPos), toVec3(teePos)).normalize();

    const defaultForward = new THREE.Vector3(0, 0, 1);
    const rotation = new THREE.Quaternion().setFromUnitVectors(defaultForward, direction);

    const obb = getOrientedBoundingBox(hole, rotation);

    const box = new THREE.Box3().setFromCenterAndSize(
      obb.center,
      obb.halfSize.clone().multiplyScalar(2),
    );

    const orientation = toVec3(teePos).clone().sub(toVec3(greenPos)).normalize();
    const target = box.getCenter(new THREE.Vector3());
    const position = fitCameraToBounds(box, camera, 1);
    const azimuthAngle = Math.atan2(orientation.x, orientation.z);
    const frac = 0.15;
    const distance = position.distanceTo(target);
    const xOffset = focalOffsetXForScreenFrac(frac, camera, distance);
    await Promise.all([
      controls.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z, true),
      controls.rotateAzimuthTo(azimuthAngle, true),
      controls.setFocalOffset(-xOffset, 0, 0, true),
    ]);

    controls.saveState();
  };
};
